import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTypedSelector } from "../../../../redux/store";
import useActiveTab from "../../../../hooks/useActiveTab";
import { hasAccess } from "../../../../access";
import { TabOptions } from "../../../../types";
import BasicPageLayout from "../../../../components/BasicPageLayout";
import { SideTab } from "../../../../components/common";

import { TabKeys, TabAccessNames } from "./constants/access";
import { SystemPerformanceProvider } from "./context";
import Toolbar from "./components/Toolbar";
import Overview from "./tabs/Overview";
import Incidents from "./tabs/Incidents";
import Operations from "./tabs/Operations";
import Clients from "./tabs/Clients";

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;

	box-sizing: border-box;
	width: 100%;
	height: 100%;
	min-width: 0;
	min-height: 0;
	max-width: 100%;
	padding: 16px;
	overflow: hidden;
`;

const TabContent = styled.div`
	flex: 1 1 0;
	min-width: 0;
	min-height: 0;
	max-width: 100%;
	overflow: auto;
`;

const SystemPerformancePage: React.FC = () => {
	const { t } = useTranslation();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const tabs: TabOptions.Array = useMemo(
		() =>
			[
				{
					key: TabKeys.OVERVIEW,
					label: t("settings.systemPerformance.tabs.overview"),
					value: { render: () => <Overview /> },
					accessName: TabAccessNames[TabKeys.OVERVIEW],
				},
				{
					key: TabKeys.INCIDENTS,
					label: t("settings.systemPerformance.tabs.incidents"),
					value: { render: () => <Incidents /> },
					accessName: TabAccessNames[TabKeys.INCIDENTS],
				},
				{
					key: TabKeys.OPERATIONS,
					label: t("settings.systemPerformance.tabs.operations"),
					value: { render: () => <Operations /> },
					accessName: TabAccessNames[TabKeys.OPERATIONS],
				},
				{
					key: TabKeys.CLIENTS,
					label: t("settings.systemPerformance.tabs.clients"),
					value: { render: () => <Clients /> },
					accessName: TabAccessNames[TabKeys.CLIENTS],
				},
			].filter((tab) => hasAccess(tab.accessName, personalRole)),
		[t, personalRole],
	);

	const { activeKey, activeTab, setActiveKey } = useActiveTab(tabs);

	return (
		<SystemPerformanceProvider>
			<BasicPageLayout
				navigation={
					<SideTab
						value={activeKey}
						onChange={setActiveKey}
						options={tabs}
						variant="vertical"
					/>
				}
				content={
					<Content>
						<Toolbar
							hideService={activeKey === TabKeys.INCIDENTS}
						/>
						<TabContent>{activeTab?.value?.render()}</TabContent>
					</Content>
				}
			/>
		</SystemPerformanceProvider>
	);
};

export default SystemPerformancePage;
